import React, { useState } from 'react';
import {
  Button,
  Icon,
  RemoveAlertDialog,
  Text,
  useBoolean,
  useDisclosure,
  VStack,
} from '@wegroup/design-system';
import { useTranslation } from 'react-i18next';
import { getSelf } from '../../../../../../../../common/src/api/getSelf';
import GrayTextBox from '../../../../../../../../company-scan/components/GrayTextBox';
import { sanitize } from 'dompurify';
import { useQueryClient } from 'react-query';
import { getUseInsurancePolicyPackagesQKey } from '../../../../../../../../parties/src/features/detail/api/insurance-policies/getInsurancePolicyPackagesByParty';
import { getUseInsurancePoliciesQKey } from '../../../../../../../../parties/src/features/detail/api/insurance-policies/getInsurancePoliciesByParty';

interface Props {
  documentUrl: string;
  title?: string;
  isPackage?: boolean;
  partyId?: string;
}

const InsurancePolicyDocumentButton: React.FC<Props> = ({
  documentUrl,
  title,
  isPackage,
  partyId,
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { isOpen, onClose, onOpen } = useDisclosure();
  const [errorMsg, setErrorMsg] = useState<string>('');
  const [isLoading, setIsLoading] = useBoolean(false);
  const isDownloadLink = documentUrl.includes('/v1/api/documents/');

  const handleClick = async () => {
    if (isDownloadLink) {
      const a = document.createElement('a');
      a.style.display = 'none';
      a.target = '_blank';
      a.href = documentUrl;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } else {
      try {
        setIsLoading.on();
        // Generate document
        await getSelf<{ url: string }>(documentUrl);
        // refetch policies
        if (isPackage) {
          await queryClient.refetchQueries(
            partyId
              ? getUseInsurancePolicyPackagesQKey({ partyId })
              : ['parties', 'insurance-policy-packages'],
          );
        } else {
          await queryClient.refetchQueries(
            partyId
              ? getUseInsurancePoliciesQKey({ partyId })
              : ['parties', 'insurance-policies'],
          );
        }
      } catch (error) {
        const errorObj = JSON.parse((error as Error)?.message);
        setErrorMsg(errorObj?.detail?.error_str);
        onOpen();
      } finally {
        setIsLoading.off();
      }
    }
  };

  return (
    <>
      <RemoveAlertDialog
        isOpen={isOpen}
        onClose={onClose}
        onConfirm={onClose}
        confirmButtonText={t('CLOSE')}
        variant="primary"
        title={t('Something went wrong')}
        hideCancelButton
        size="xl"
      >
        <VStack align="stretch" spacing="3">
          <Text textStyle="normalBody">
            {t('INSURANCE-POLICIES_DOCUMENT_ERROR_BODY')}
          </Text>
          <GrayTextBox
            overflowX="auto"
            sx={{
              pre: {
                overflowX: 'auto',
                bg: 'white',
                p: '2',
                borderRadius: 'md',
              },
            }}
          >
            <div
              dangerouslySetInnerHTML={{
                __html: sanitize(errorMsg),
              }}
            />
          </GrayTextBox>
        </VStack>
      </RemoveAlertDialog>
      <Button
        leftIcon={
          <Icon name="GeneralActionsDownload" boxSize="5" color="primary.500" />
        }
        size="sm"
        alignSelf="flex-start"
        variant="white"
        w="100%"
        color="primary.500"
        py="1"
        isLoading={isLoading}
        onClick={handleClick}
      >
        {isDownloadLink
          ? title || t('INSURANCE-POLICIES_DOCUMENT_BUTTON')
          : t('INSURANCE-POLICIES_DOCUMENT_BUTTON_GENERATE')}
      </Button>
    </>
  );
};

export default InsurancePolicyDocumentButton;
